// Cross-domain per-day label for the Signal Timeline. Sister helper to
// dailyLabels.js, but where dayLabel describes a single domain on one
// day, daySignalLabel summarizes the WHOLE day across all three
// domains — what we surface under each card's Signal Score.
//
// Same priority-ladder pattern: most-specific narrative wins, then
// fall through to lighter rules, with a band-only fallback at the
// bottom. All thresholds match the constants in dailyLabels.js so
// the two surfaces stay in agreement about what "rising", "above
// baseline", and "outlier" mean.

import { PATIENT, lintText } from "./insightRubric";

const SCORE_KEYS = {
  BIO: "bio_score",
  PSYCH: "psych_score",
  BEH: "beh_score",
};

const BASELINE = 0.50;
const STRONG = 0.75;
const DAY_DELTA_NOISE = 0.02;

// Threshold above which a single domain's score gap from the others
// reads as a real cross-domain story rather than a flat day. 0.15
// mirrors the inter-domain gap used by the reveal-insight pattern
// detector — keeps narratives consistent across surfaces.
const CROSS_DOMAIN_GAP = 0.15;

// Outlier threshold — a single day only earns "strongest/lowest day
// this week" if it's at least this far from the window mean. Matches
// dailyLabels.OUTLIER_GAP.
const OUTLIER_GAP = 0.05;

// Aligned-week threshold for the "all three peaked simultaneously"
// narrative. Each domain must be at-or-near the high band AND close
// to the window's per-domain maximum.
const ALIGNED_NEAR_MAX = 0.03;


function _avg(xs) {
  if (!xs.length) return 0;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

function _domainScore(snapshot, domain) {
  const k = SCORE_KEYS[domain];
  return typeof snapshot[k] === "number" ? snapshot[k] : 0;
}


// Rubric guard at the boundary — see dailyLabels.js for the rationale.
export function daySignalLabel(idx, snapshots) {
  const label = _daySignalLabel(idx, snapshots);
  return label && lintText(label, PATIENT).length ? "" : label;
}

function _daySignalLabel(idx, snapshots) {
  if (!Array.isArray(snapshots) || !snapshots.length) return "";
  if (idx < 0 || idx >= snapshots.length) return "";

  const today = snapshots[idx];
  const N = snapshots.length;
  const bioSeries = snapshots.map(s => _domainScore(s, "BIO"));
  const psychSeries = snapshots.map(s => _domainScore(s, "PSYCH"));
  const behSeries = snapshots.map(s => _domainScore(s, "BEH"));

  const bio = bioSeries[idx];
  const psych = psychSeries[idx];
  const beh = behSeries[idx];

  // ── Rule 1: all three peaked simultaneously ───────────────────────
  // Every domain is in the high band AND within ALIGNED_NEAR_MAX of
  // its own window max. Captures the rare "everything came together"
  // day the user wants to remember.
  if (N >= 3) {
    const bioMax = Math.max(...bioSeries);
    const psychMax = Math.max(...psychSeries);
    const behMax = Math.max(...behSeries);
    const allHigh = bio >= STRONG && psych >= STRONG && beh >= STRONG;
    const allNearMax =
      bioMax - bio <= ALIGNED_NEAR_MAX &&
      psychMax - psych <= ALIGNED_NEAR_MAX &&
      behMax - beh <= ALIGNED_NEAR_MAX;
    if (allHigh && allNearMax) {
      return "All three domains peaked simultaneously";
    }
  }

  // ── Rule 2: single-domain outlier in window (high or low) ─────────
  // Highest day requires a real gap above the window mean; same for
  // lowest. Returns domain-specific phrasing.
  if (N >= 3) {
    const bioMean = _avg(bioSeries);
    const psychMean = _avg(psychSeries);
    const behMean = _avg(behSeries);
    if (bio === Math.max(...bioSeries) && bio - bioMean >= OUTLIER_GAP) {
      return "Body signal strongest day this week";
    }
    if (psych === Math.max(...psychSeries) && psych - psychMean >= OUTLIER_GAP) {
      return "Mind signal strongest day this week";
    }
    if (beh === Math.max(...behSeries) && beh - behMean >= OUTLIER_GAP) {
      return "Behavior signal strongest day this week";
    }
    if (bio === Math.min(...bioSeries) && bioMean - bio >= OUTLIER_GAP) {
      return "Body's lowest day this week";
    }
    if (psych === Math.min(...psychSeries) && psychMean - psych >= OUTLIER_GAP) {
      return "Mind's lowest day this week";
    }
    if (beh === Math.min(...behSeries) && behMean - beh >= OUTLIER_GAP) {
      return "Behavior's lowest day this week";
    }
  }

  // ── Rule 3: cross-domain gap narratives ───────────────────────────
  // Pairs that frequently come up in the data: psych ahead of beh
  // ("clarity up, follow-through flat"), bio behind ("body lagging,
  // mind running ahead"), beh ahead of psych ("acting without
  // checking in"). Order matters — the highest-priority interpretive
  // story for coherence work is psych-vs-beh.
  if (psych - beh >= CROSS_DOMAIN_GAP) {
    return "Mind clarity up, follow-through flat";
  }
  if (beh - psych >= CROSS_DOMAIN_GAP) {
    return "Acting without checking in";
  }
  const cognitiveMean = (psych + beh) / 2;
  if (cognitiveMean - bio >= CROSS_DOMAIN_GAP) {
    return "Body lagging, mind running ahead";
  }
  if (bio - cognitiveMean >= CROSS_DOMAIN_GAP) {
    return "Body recovering, mind running ahead";
  }

  // ── Rule 4: Signal Score (rci_total) trend across the window ──────
  if (idx >= 2 && N >= 3) {
    const rciSeries = snapshots.map(s =>
      typeof s.rci_total === "number" ? s.rci_total : 0
    );
    let ups = 0;
    for (let k = idx; k > 0 && rciSeries[k] - rciSeries[k - 1] >= DAY_DELTA_NOISE; k--) {
      ups++;
    }
    if (ups >= 3) return `Signal climbing ${ups} days running`;

    let downs = 0;
    for (let k = idx; k > 0 && rciSeries[k - 1] - rciSeries[k] >= DAY_DELTA_NOISE; k--) {
      downs++;
    }
    if (downs >= 3) return `Signal sliding ${downs} days running`;
  }

  // ── Rule 5: single-day Signal Score move vs yesterday ─────────────
  if (idx >= 1) {
    const rciToday =
      typeof today.rci_total === "number" ? today.rci_total : 0;
    const rciYesterday =
      typeof snapshots[idx - 1].rci_total === "number"
        ? snapshots[idx - 1].rci_total
        : 0;
    const delta = rciToday - rciYesterday;
    if (delta >= 0.05) return "Signal up from yesterday";
    if (delta <= -0.05) return "Signal down from yesterday";
    if (Math.abs(delta) < DAY_DELTA_NOISE) {
      return "Signal holding steady";
    }
  }

  // ── Rule 6: band-only fallback on rci_total ───────────────────────
  const rci =
    typeof today.rci_total === "number"
      ? today.rci_total
      : (bio + psych + beh) / 3;
  if (idx === 0) {
    if (rci >= STRONG) return "Strong opening signal";
    if (rci >= BASELINE) return "Mid-range opening day";
    return "Low opening signal";
  }
  if (rci >= STRONG) return "Strong signal day";
  if (rci >= BASELINE) return "Mid-range signal day";
  return "Below-baseline day";
}
